import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, Grid, IconButton, MenuItem, Paper, Snackbar, Stack, Switch,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField,
  Tooltip, Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  FileDownload as ImportIcon,
  Psychology as StressIcon,
  SelfImprovement as TechniqueIcon,
} from '@mui/icons-material';
import { stressContentAPI } from '../services/api';
import ConfirmDialog from '../components/ConfirmDialog';
import PageHeader from '../components/PageHeader';

const TYPES = [
  { value: 'hero', label: 'Hero' },
  { value: 'technique', label: 'Technique' },
  { value: 'activity', label: 'Activity' },
  { value: 'tip', label: 'Daily Habit' },
];

const COLORS = [
  { value: 'primary', label: 'Primary' },
  { value: 'teal', label: 'Teal' },
  { value: 'green', label: 'Green' },
  { value: 'amber', label: 'Amber' },
  { value: 'rose', label: 'Rose' },
  { value: 'purple', label: 'Purple' },
];

const ICONS = [
  'psychology', 'air', 'spa', 'bedtime', 'favorite', 'anchor', 'edit_note',
  'directions_walk', 'water_drop', 'music_note', 'self_improvement',
  'local_fire_department', 'accessibility_new', 'people', 'palette',
  'label', 'timer', 'coffee',
];

const emptyContent = () => ({
  type: 'technique',
  title: '',
  subtitle: '',
  description: '',
  badgeText: '',
  science: '',
  steps: [],
  durationMins: 5,
  durationText: '',
  intensity: '',
  icon: 'psychology',
  color: 'primary',
  sortOrder: 0,
  isPublished: true,
});

const typeLabel = (type) => TYPES.find(t => t.value === type)?.label || type;

export default function StressContent() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [filter, setFilter] = useState('all');
  const [editing, setEditing] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, item: null });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter === 'all' ? undefined : { type: filter };
      const res = await stressContentAPI.getAll(params);
      setItems(res.data.data || []);
    } catch (e) {
      setSnackbar({
        open: true,
        message: e.response?.data?.message || 'Failed to load stress content',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const stats = useMemo(() => ({
    total: items.length,
    published: items.filter(i => i.isPublished).length,
    techniques: items.filter(i => i.type === 'technique').length,
    activities: items.filter(i => i.type === 'activity').length,
  }), [items]);

  const openAdd = () => setEditing(emptyContent());
  const openEdit = (item) => setEditing({ ...emptyContent(), ...item });

  const handleSave = async (payload) => {
    if (payload._id) {
      await stressContentAPI.update(payload._id, payload);
      setSnackbar({ open: true, message: 'Stress content updated', severity: 'success' });
    } else {
      await stressContentAPI.create(payload);
      setSnackbar({ open: true, message: 'Stress content created', severity: 'success' });
    }
    setEditing(null);
    load();
  };

  const handleDelete = (item) => {
    setDeleteConfirm({ open: true, item });
  };

  const doDelete = async () => {
    const item = deleteConfirm.item;
    setDeleteConfirm({ open: false, item: null });
    try {
      await stressContentAPI.delete(item._id);
      setSnackbar({ open: true, message: 'Stress content deleted', severity: 'success' });
      load();
    } catch (e) {
      setSnackbar({
        open: true,
        message: e.response?.data?.message || 'Failed to delete content',
        severity: 'error',
      });
    }
  };

  const handleImportStarter = async () => {
    setImporting(true);
    try {
      const res = await stressContentAPI.importStarter();
      setSnackbar({
        open: true,
        message: res.data?.message || 'Starter stress content imported',
        severity: 'success',
      });
      await load();
    } catch (e) {
      setSnackbar({
        open: true,
        message: e.response?.data?.message || 'Failed to import starter content',
        severity: 'error',
      });
    } finally {
      setImporting(false);
    }
  };

  return (
    <Box>
      <PageHeader
        title="Stress Management Content"
        subtitle="Manage the content shown on the Fitistic stress management screen."
        gradient="linear-gradient(90deg, #7C3AED, #A78BFA)"
        actions={<>
          <Button variant="outlined" size="small" startIcon={<ImportIcon />} onClick={handleImportStarter} disabled={importing}>
            {importing ? 'Importing...' : 'Import Starter'}
          </Button>
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openAdd}>Add Content</Button>
        </>}
      />

      <Grid container spacing={2} mb={2.5}>
        {[
          ['Total Content', stats.total],
          ['Published', stats.published],
          ['Techniques', stats.techniques],
          ['Activities', stats.activities],
        ].map(([label, value]) => (
          <Grid item xs={6} md={3} key={label}>
            <Paper elevation={0} sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 2 }}>
              <Typography sx={{ fontSize: '1.5rem', fontWeight: 800, lineHeight: 1 }}>{value}</Typography>
              <Typography variant="body2" color="text.secondary" mt={0.5}>{label}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Paper elevation={0} sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <TextField select label="Type" value={filter} onChange={(e) => setFilter(e.target.value)} sx={{ minWidth: 180 }}>
            <MenuItem value="all">All Types</MenuItem>
            {TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
          </TextField>
          <Box sx={{ flexGrow: 1 }} />
          <Typography variant="body2" color="text.secondary">
            {loading ? 'Loading...' : `${items.length} items`}
          </Typography>
        </Stack>
      </Paper>

      <Paper elevation={0} sx={{ border: 1, borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Content</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell>Order</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading && items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Box textAlign="center" py={8}>
                      <TechniqueIcon sx={{ fontSize: 44, color: 'text.disabled', mb: 1 }} />
                      <Typography fontWeight={700}>No stress content yet</Typography>
                      <Typography variant="body2" color="text.secondary" mb={2}>Create content here to populate the mobile screen.</Typography>
                      <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}>Add First Item</Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : items.map(item => (
                <TableRow key={item._id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={700}>{item.title}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 520 }} noWrap>
                      {item.subtitle || item.description || item.science || 'No supporting text'}
                    </Typography>
                  </TableCell>
                  <TableCell><Chip size="small" label={typeLabel(item.type)} /></TableCell>
                  <TableCell>{item.durationMins ? `${item.durationMins} min` : item.durationText || '-'}</TableCell>
                  <TableCell>{item.sortOrder}</TableCell>
                  <TableCell>
                    <Chip size="small" label={item.isPublished ? 'Published' : 'Draft'} color={item.isPublished ? 'success' : 'default'} />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => openEdit(item)}><EditIcon fontSize="small" /></IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => handleDelete(item)}><DeleteIcon fontSize="small" /></IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {editing && (
        <StressContentDialog
          item={editing}
          onClose={() => setEditing(null)}
          onSave={handleSave}
          onError={(message) => setSnackbar({ open: true, message, severity: 'error' })}
        />
      )}

      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>{snackbar.message}</Alert>
      </Snackbar>

      <ConfirmDialog
        open={deleteConfirm.open}
        title={`Delete "${deleteConfirm.item?.title}"?`}
        message="This will permanently delete this content item."
        confirmLabel="Delete"
        onConfirm={doDelete}
        onCancel={() => setDeleteConfirm({ open: false, item: null })}
      />
    </Box>
  );
}

function StressContentDialog({ item: initialItem, onClose, onSave, onError }) {
  const [item, setItem] = useState({ ...initialItem });
  const [stepsText, setStepsText] = useState((initialItem.steps || []).join('\n'));
  const [saving, setSaving] = useState(false);

  const update = (field, value) => setItem(prev => ({ ...prev, [field]: value }));
  const isHero = item.type === 'hero';
  const isTechnique = item.type === 'technique';
  const isActivity = item.type === 'activity';

  const handleSave = async () => {
    if (!item.title.trim()) {
      onError('Title is required');
      return;
    }

    setSaving(true);
    try {
      await onSave({
        ...item,
        title: item.title.trim(),
        steps: stepsText.split('\n').map(s => s.trim()).filter(Boolean),
        durationMins: Number(item.durationMins || 0),
        sortOrder: Number(item.sortOrder || 0),
      });
    } catch (e) {
      onError(e.response?.data?.message || 'Failed to save content');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open fullWidth maxWidth="md" onClose={saving ? undefined : onClose}>
      <DialogTitle>{item._id ? 'Edit Stress Content' : 'Add Stress Content'}</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField select label="Type" value={item.type} onChange={(e) => update('type', e.target.value)} fullWidth>
              {TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField select label="Color" value={item.color} onChange={(e) => update('color', e.target.value)} fullWidth>
              {COLORS.map(c => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField select label="Icon" value={item.icon} onChange={(e) => update('icon', e.target.value)} fullWidth>
              {ICONS.map(icon => <MenuItem key={icon} value={icon}>{icon}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={8}>
            <TextField label="Title" value={item.title} onChange={(e) => update('title', e.target.value)} fullWidth />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField label="Sort Order" type="number" value={item.sortOrder} onChange={(e) => update('sortOrder', e.target.value)} fullWidth />
          </Grid>
          <Grid item xs={12} sm={isHero ? 6 : 12}>
            <TextField label="Subtitle" value={item.subtitle} onChange={(e) => update('subtitle', e.target.value)} fullWidth />
          </Grid>
          {isHero && (
            <Grid item xs={12} sm={6}>
              <TextField label="Badge Text" value={item.badgeText} onChange={(e) => update('badgeText', e.target.value)} fullWidth />
            </Grid>
          )}
          <Grid item xs={12}>
            <TextField label="Description" value={item.description} onChange={(e) => update('description', e.target.value)} multiline rows={3} fullWidth />
          </Grid>
          {isTechnique && (
            <>
              <Grid item xs={12} sm={4}>
                <TextField label="Duration Minutes" type="number" value={item.durationMins} onChange={(e) => update('durationMins', e.target.value)} fullWidth />
              </Grid>
              <Grid item xs={12} sm={8}>
                <TextField label="Science Note" value={item.science} onChange={(e) => update('science', e.target.value)} fullWidth />
              </Grid>
              <Grid item xs={12}>
                <TextField label="Steps (one per line)" value={stepsText} onChange={(e) => setStepsText(e.target.value)} multiline rows={6} fullWidth />
              </Grid>
            </>
          )}
          {isActivity && (
            <>
              <Grid item xs={12} sm={6}>
                <TextField label="Duration Text" value={item.durationText} onChange={(e) => update('durationText', e.target.value)} fullWidth />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Intensity" value={item.intensity} onChange={(e) => update('intensity', e.target.value)} fullWidth />
              </Grid>
            </>
          )}
          <Grid item xs={12}>
            <FormControlLabel
              control={<Switch checked={item.isPublished} onChange={(e) => update('isPublished', e.target.checked)} />}
              label="Published"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save Content'}</Button>
      </DialogActions>
    </Dialog>
  );
}
